// Upload slots ("Share files" on the status page, once staff request files).
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Upload } from 'lucide-react';
import type { IntakeFileView, Slot, SlotId } from '@upm/domain';
import { api, ApiError, uploadWithProgress } from '../lib/api';
import { Button, Pill, cx } from '../ui';

export type SlotView = Slot & { files: IntakeFileView[] };

export const PLANNING_COPIES = (
  <>
    <strong>These are planning copies.</strong> Send files exactly as they come out of your software. There’s no need to clean them up. Right before launch we’ll ask for fresh copies on your freeze date, so nothing that changes in between is lost. Please hold off on tax returns and client documents for now. Those move later, through a separate secure transfer, once we agree your document scope. Only your implementation team can see these files.
  </>
);

/** Under the status page's Documents uploader: the same ask as PLANNING_COPIES, for files sent at any time. */
export const PLANNING_HOLD_OFF = 'Please hold off on tax returns and client documents. Those move later, through a separate secure transfer.';

export const ACCEPT = '.csv,.xlsx,.xls,.pdf,.docx,.txt,.zip,.json';

type Uploading = { key: string; slotId: SlotId; name: string; progress: number; error: string | null };

export function uploadError(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Upload failed. Try again.';
  switch (e.code) {
    case 'file_too_large': return 'This file is over 100 MB. Split it or contact us.';
    case 'file_type': return 'We can’t accept this type of file. Use CSV, Excel, PDF, Word, text, ZIP or JSON.';
    case 'file_limit': return 'You’ve reached the limit of 50 files. Remove one to add another.';
    case 'answers_locked': return 'Files can’t be changed any more. Contact your Customer Experience Manager.';
    case 'files_not_requested': return 'File uploads are closed right now. Your Customer Experience Manager will let you know when to share files.';
    case 'network': return 'Upload interrupted. Check your connection and try again.';
    default: return e.message || 'Upload failed. Try again.';
  }
}

export function FileSlots({ slots, onUploaded, onRemoved, editable = true, dropAnywhere = false }: {
  slots: SlotView[];
  onUploaded: (f: IntakeFileView) => void;
  onRemoved: (id: string) => void;
  editable?: boolean;
  dropAnywhere?: boolean;
}) {
  const [uploads, setUploads] = useState<Uploading[]>([]);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [announce, setAnnounce] = useState('');

  const upload = useCallback((slotId: SlotId, list: FileList | File[]) => {
    for (const file of Array.from(list)) {
      const key = `${slotId}:${file.name}:${Math.random().toString(36).slice(2)}`;
      setUploads((u) => [...u, { key, slotId, name: file.name, progress: 0, error: null }]);
      const form = new FormData();
      form.append('slotId', slotId);
      form.append('file', file, file.name);
      const { promise } = uploadWithProgress<IntakeFileView>('/api/intake/files', form, (p) =>
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))));
      promise.then(
        // A success in a slot also clears that slot's earlier rejected-file errors.
        (f) => { setUploads((u) => u.filter((x) => x.key !== key && !(x.slotId === slotId && x.error))); onUploaded(f); setAnnounce(`${file.name} uploaded.`); },
        (e) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: uploadError(e) } : x))),
      );
    }
  }, [onUploaded]);

  useEffect(() => {
    if (!dropAnywhere || !editable) return;
    let depth = 0;
    const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
    const enter = (e: DragEvent) => { if (hasFiles(e)) { depth++; setDragging(true); } };
    const leave = (e: DragEvent) => { if (hasFiles(e)) { depth = Math.max(0, depth - 1); if (!depth) setDragging(false); } };
    const over = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    const drop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const target = (e.target as HTMLElement | null)?.closest?.('[data-slot-id]');
      const slotId = (target?.getAttribute('data-slot-id') as SlotId | null) ?? 'anything_else';
      if (e.dataTransfer?.files.length) upload(slotId, e.dataTransfer.files);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragleave', leave);
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, [dropAnywhere, editable, upload]);

  const remove = async (f: IntakeFileView) => {
    setRemoveError(null);
    try { await api.del(`/api/intake/files/${f.id}`); onRemoved(f.id); setAnnounce(`${f.fileName} removed.`); }
    catch (e) { setRemoveError(e instanceof ApiError && e.code !== 'internal' ? e.message : `We couldn’t remove ${f.fileName}. Try again.`); }
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div aria-live="polite" className="sr-only">{announce}</div>
      {editable && <div className="text-sm text-muted">CSV, Excel, PDF, Word, text, ZIP or JSON, up to 100 MB each.</div>}
      {removeError && <div role="alert" className="text-sm font-semibold text-bad">{removeError}</div>}
      {slots.map((sl) => {
        const accepted = sl.files.filter((f) => f.scan !== 'rejected');
        const has = accepted.length > 0;
        const mine = uploads.filter((u) => u.slotId === sl.id);
        const inputId = `file-${sl.id}`;
        const checking = sl.files.some((f) => f.scan === 'pending');
        return (
          <div
            key={sl.id}
            data-slot-id={sl.id}
            className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-card border border-line bg-white px-4 py-4 sm:px-5"
          >
            <span aria-hidden="true" className={cx('flex h-10 w-10 flex-none items-center justify-center rounded-[10px]', has ? 'bg-ok-bg text-ok' : 'bg-subtle text-muted')}>
              <FileText size={20} strokeWidth={1.8} />
            </span>
            <div className="min-w-0 flex-[1_1_240px]">
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <span className="text-[15px] font-semibold">{sl.title}</span>
                {has && !checking && <Pill tone="ok" dot className="!py-0 !text-xs">Received</Pill>}
              </div>
              {sl.files.length === 0 && mine.length === 0 && <div className="mt-0.5 text-sm text-muted">{sl.detail}</div>}
              <ul className="m-0 list-none p-0">
                {sl.files.map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center gap-x-3 text-[13px]">
                    <span className={cx('min-w-0 font-semibold [overflow-wrap:anywhere]', f.scan === 'clean' ? 'text-ok-fg' : f.scan === 'pending' ? 'text-muted' : 'text-bad')}>
                      {f.scan === 'clean' ? `Received · ${f.fileName}` : f.scan === 'pending' ? `Checking file… · ${f.fileName}` : `We couldn’t accept this file · ${f.fileName}`}
                    </span>
                    {editable && (
                      <Button variant="quiet" size="sm" onClick={() => void remove(f)} aria-label={`Remove ${f.fileName}`}>Remove</Button>
                    )}
                  </li>
                ))}
                {mine.map((u) => (
                  <li key={u.key} className="text-[13px]">
                    <div className="flex flex-wrap items-center gap-x-3">
                      <span className="min-w-0 text-ink-2 [overflow-wrap:anywhere]">{u.error ? u.name : `Uploading ${u.name}`}</span>
                      {u.error ? (
                        <Button variant="quiet" size="sm" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))}>Dismiss</Button>
                      ) : (
                        <span className="text-muted">{Math.round(u.progress * 100)}%</span>
                      )}
                    </div>
                    {u.error
                      ? <div role="alert" className="font-semibold text-bad">{u.error}</div>
                      : (
                        <div role="progressbar" aria-label={`Uploading ${u.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(u.progress * 100)} className="mt-1 h-1.5 w-full max-w-[280px] overflow-hidden rounded-full bg-line">
                          <div className="h-1.5 rounded-full bg-brand transition-[width]" style={{ width: `${Math.round(u.progress * 100)}%` }} />
                        </div>
                      )}
                  </li>
                ))}
              </ul>
            </div>
            {editable && (
              <div className="flex items-center gap-3.5">
                {sl.exportGuideUrl && (
                  <a href={sl.exportGuideUrl} target="_blank" rel="noreferrer" className="text-[13px] font-semibold">How to export</a>
                )}
                <input
                  ref={(el) => { inputs.current[sl.id] = el; }}
                  id={inputId}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                  onChange={(e) => { if (e.target.files?.length) upload(sl.id, e.target.files); e.target.value = ''; }}
                />
                <Button
                  variant={sl.files.length ? 'secondary' : 'outline'}
                  size="sm"
                  className="!min-h-10 !rounded-[10px] !px-3.5"
                  onClick={() => inputs.current[sl.id]?.click()}
                  aria-label={`${sl.files.length ? 'Add another file to' : 'Choose file for'} ${sl.title}`}
                >
                  {sl.files.length ? 'Add another file' : 'Choose file'}
                </Button>
              </div>
            )}
          </div>
        );
      })}
      {dragging && (
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-40 flex items-center justify-center bg-brand/10">
          <div className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-brand bg-white px-8 py-6 font-semibold text-brand shadow-lg">
            <Upload size={22} /> Drop files to upload. We’ll sort them.
          </div>
        </div>
      )}
    </div>
  );
}
